import React, { useState, useEffect, useCallback } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  UserIcon,
  CalendarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { noteApi } from '../services/noteApi';
import { Note } from '../types/Note';
import Layout from './Layout';
import NotesForm from './NotesForm';
import PermissionGate from './PermissionGate';

const NotesList: React.FC = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const { currentBranch } = useBranch();

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await noteApi.getNotes(currentPage, 20, searchTerm);
      
      if (response.success) {
        let filteredNotes = response.data;
        
        // Apply type filter
        if (typeFilter === 'lead') {
          filteredNotes = filteredNotes.filter(note => !note.taskId);
        } else if (typeFilter === 'task') {
          filteredNotes = filteredNotes.filter(note => note.taskId);
        }
        
        setNotes(filteredNotes);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
        }
      } else {
        setError(response.message || 'Failed to fetch notes');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, typeFilter]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchNotes();
  };

  const handleCreateNote = () => {
    setEditingNote(null);
    setShowForm(true);
  };

  const handleEditNote = (note: Note) => {
    setEditingNote(note);
    setShowForm(true);
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await noteApi.deleteNote(noteId);
      if (response.success) {
        await fetchNotes();
      } else {
        setError(response.message || 'Failed to delete note');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete note');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingNote(null);
    fetchNotes();
  };

  const toggleNoteExpansion = (noteId: number) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimestamp = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', ' ·');
  };

  const isNoteExpanded = (noteId: number) => expandedNotes.has(noteId);

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchTerm || typeFilter;

  if (loading && notes.length === 0) {
    return (
      <Layout title="Notes" subtitle="Manage your notes and observations">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="divide-y divide-gray-200">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="py-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Notes" subtitle="Manage your notes and observations">
      <div className="bg-white">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="max-w-[1200px] mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-gray-900">Notes</h1>
                {currentBranch && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                    {currentBranch.name}
                  </span>
                )}
                <span className="text-sm text-gray-500">({notes.length})</span>
              </div>
              
              <PermissionGate module="notes" permission="create">
                <button
                  onClick={handleCreateNote}
                  className="inline-flex items-center px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Note
                </button>
              </PermissionGate>
            </div>

            {/* Filter Bar */}
            <div className="flex items-center gap-2 flex-wrap">
              <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search notes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </form>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              >
                <option value="">All Types</option>
                <option value="lead">Lead Note</option>
                <option value="task">Task Note</option>
              </select>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-3 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Clear
                </button>
              )}
            </div>

            {/* Active Filters Chips */}
            {hasActiveFilters && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {searchTerm && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                    Search: {searchTerm}
                  </span>
                )}
                {typeFilter && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                    Type: {typeFilter === 'lead' ? 'Lead Note' : 'Task Note'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 py-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Notes List */}
          {notes.length === 0 ? (
            <div className="text-center py-20">
              <div className="mx-auto h-24 w-24 text-gray-300 mb-4">
                <EyeIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">No notes yet</h3>
              <p className="text-sm text-gray-500 mb-6">Notes you add or sync will appear here.</p>
              <PermissionGate module="notes" permission="create">
                <button
                  onClick={handleCreateNote}
                  className="inline-flex items-center px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Note
                </button>
              </PermissionGate>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {notes.map((note) => {
                const isExpanded = isNoteExpanded(note.id);
                const displayContent = isExpanded 
                  ? note.content 
                  : note.content.slice(0, 150);

                return (
                  <li 
                    key={note.id} 
                    className="group relative flex gap-4 py-4 hover:bg-gray-50 focus-within:bg-gray-50 transition-colors"
                    tabIndex={0}
                  >
                    {/* Avatar */}
                    <div className="hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                      {(note.leadName || 'N').charAt(0).toUpperCase()}
                    </div>

                    {/* Main Column */}
                    <div className="flex-1 min-w-0">
                      {/* Top Line - Title/Meta */}
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          {note.leadName ? (
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {note.leadName}
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-gray-500 italic">
                              No lead
                            </span>
                          )}
                          {note.leadEmail && (
                            <span className="text-sm text-gray-600 truncate">
                              ({note.leadEmail})
                            </span>
                          )}
                          {note.taskId && (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              Task Note
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatTimestamp(note.createdAt)}
                        </span>
                      </div>

                      {/* Content Snippet */}
                      <div className="mt-1 text-sm text-gray-900 line-clamp-2">
                        {displayContent}
                        {note.content.length > 150 && (
                          <button
                            onClick={() => toggleNoteExpansion(note.id)}
                            className="ml-1 text-sm text-gray-600 hover:text-gray-900 underline"
                          >
                            {isExpanded ? 'Read less' : 'Read more'}
                          </button>
                        )}
                      </div>

                      {/* Secondary Meta Row */}
                      <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                        <span>by {note.creatorName || 'Unknown'}</span>
                        <span>•</span>
                        <span>{note.taskId ? 'Task Note' : 'Lead Note'}</span>
                        {note.leadName && (
                          <>
                            <span>•</span>
                            <span className="truncate">Lead: {note.leadName}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Actions - Show on Hover */}
                    <div className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity flex items-center gap-1">
                      <PermissionGate module="notes" permission="edit">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditNote(note);
                          }}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
                          title="Edit note"
                          aria-label="Edit note"
                        >
                          <PencilIcon className="h-4 w-4 text-gray-500" />
                        </button>
                      </PermissionGate>
                      <PermissionGate module="notes" permission="delete">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNote(note.id);
                          }}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
                          title="Delete note"
                          aria-label="Delete note"
                        >
                          <TrashIcon className="h-4 w-4 text-gray-500" />
                        </button>
                      </PermissionGate>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notes Form Modal */}
      {showForm && (
        <NotesForm
          note={editingNote}
          onClose={() => {
            setShowForm(false);
            setEditingNote(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}
    </Layout>
  );
};

export default NotesList;
